import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Caption1, Field, Input, MessageBar, MessageBarBody, Select, Text, Textarea, makeStyles } from '@fluentui/react-components';
import { composerMergeTokens, type ComposePreview, type HrWorkflowSettings, type Person } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { previewCommunication, sendCommunication } from './api.js';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  preview: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2, minWidth: 0,
  },
  body: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
});

export interface ComposerProps {
  readonly person: Person;
  /** HR's saved starting points, from agency settings. */
  readonly templates: HrWorkflowSettings['emailTemplates'];
  readonly canEdit: boolean;
}

const mergeHint = `Merge fields: ${composerMergeTokens.map((t) => `{{${t}}}`).join(', ')}. Portal links and access codes are sent separately from the Portal step.`;

/**
 * Compose one email to one person (#62). The server renders the preview against the person's
 * record and names any merge field it cannot fill; Send stays disabled until the exact text on
 * screen has a clean preview. Sending records the message as queued — the mail service sends it —
 * and there is no attachment control because a message has nowhere to carry a file.
 */
export function Composer({ person, templates, canEdit }: ComposerProps) {
  const styles = useStyles();
  const client = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<ComposePreview | null>(null);
  // One id per reviewed message: a double click or a retry is the same message, not a second one.
  const [operationId, setOperationId] = useState(() => crypto.randomUUID());
  const [queued, setQueued] = useState(false);

  const edit = (next: { subject?: string; body?: string }) => {
    if (next.subject !== undefined) setSubject(next.subject);
    if (next.body !== undefined) setBody(next.body);
    setPreview(null); setQueued(false); setOperationId(crypto.randomUUID());
  };
  const check = useMutation({ mutationFn: () => previewCommunication(person.id, { subject, body }), onSuccess: setPreview });
  const send = useMutation({
    mutationFn: () => sendCommunication(person.id, { operationId, subject, body }),
    onSuccess: async () => {
      setQueued(true); setPreview(null); setSubject(''); setBody(''); setOperationId(crypto.randomUUID());
      await Promise.all([
        client.invalidateQueries({ queryKey: ['hr-workflow', person.id] }),
        client.invalidateQueries({ queryKey: ['timeline', person.id] }),
        client.invalidateQueries({ queryKey: ['audit', person.id] }),
      ]);
    },
  });
  const noAddress = !person.contact.email;
  const busy = !canEdit || check.isPending || send.isPending;

  return <div className={styles.form}>
    <Field label="Starting template">
      <Select disabled={busy} onChange={(_, d) => { const t = templates.find((v) => v.id === d.value); edit({ subject: t?.subject ?? '', body: t?.text ?? '' }); }}>
        <option value="">Custom</option>
        {templates.map((template) => <option key={template.id} value={template.id}>{template.label}</option>)}
      </Select>
    </Field>
    <Text>To: {person.contact.email || 'No email address on file'} · From: HR</Text>
    <Field label="Subject" required><Input value={subject} maxLength={200} disabled={busy} onChange={(_, d) => edit({ subject: d.value })} /></Field>
    <Field label="Message" required hint={mergeHint}><Textarea value={body} resize="vertical" disabled={busy} onChange={(_, d) => edit({ body: d.value })} /></Field>
    <Button disabled={busy || noAddress || !subject.trim() || !body.trim()} onClick={() => check.mutate()}>Preview message</Button>
    {check.isError ? <MessageBar intent="error"><MessageBarBody>{check.error.message}</MessageBarBody></MessageBar> : null}
    {preview?.kind === 'unresolved' ? <MessageBar intent="warning"><MessageBarBody>
      Cannot send yet. These merge fields cannot be filled for this person: {preview.unresolved.map((t) => `{{${t}}}`).join(', ')}.
    </MessageBarBody></MessageBar> : null}
    {preview?.kind === 'refused' ? <MessageBar intent="warning"><MessageBarBody>{preview.reason}</MessageBarBody></MessageBar> : null}
    {preview?.kind === 'rendered' ? <section className={styles.preview} aria-label="Message preview">
      <Text weight="semibold">{preview.subject}</Text>
      <div className={styles.body}>{preview.text}</div>
    </section> : null}
    <Button appearance="primary" disabled={busy || noAddress || preview?.kind !== 'rendered'} onClick={() => send.mutate()}>Send reviewed message</Button>
    {send.isError ? <MessageBar intent="error"><MessageBarBody>{send.error.message}</MessageBarBody></MessageBar> : null}
    {queued ? <MessageBar intent="success"><MessageBarBody>Queued. The mail service sends it; its status shows in the history below and on the timeline.</MessageBarBody></MessageBar> : null}
    <Caption1>Messages carry no attachments. Documents stay in the app.</Caption1>
  </div>;
}
